import { api } from "@/lib/api";

export type StepKey =
  | "category" | "model" | "design" | "area" | "realization" | "detail"
  | "description" | "summary" | "contact";

export const STEP_KEYS: StepKey[] = [
  "category", "model", "design", "area", "realization", "detail",
  "description", "summary", "contact",
];

export const STEP_LABELS: Record<StepKey, string> = {
  category: "Categoria",
  model: "Modello",
  design: "Tipo di design",
  area: "Area",
  realization: "Tipo di realizzazione",
  detail: "Livello di dettaglio",
  description: "Descrizione",
  summary: "Riepilogo",
  contact: "Contatti",
};

export const CATEGORIES = [
  { id: "baby", name: "Baby", description: "Fino al numero 23" },
  { id: "kids", name: "Kids", description: "Dal numero 24 al 35" },
  { id: "adulti", name: "Adulti", description: "Dal numero 36 in su" },
];

export const MAX_DESCRIPTION = 1000;

/** An option whose name is (or starts with) "Altro" requires a free-text "Specifica". */
export const isOtherOption = (name?: string): boolean =>
  !!name && name.trim().toLowerCase().startsWith("altro");

/** "Grafiche decorative" design skips the area/realization/detail steps. */
export const isGraficheDecorative = (name?: string): boolean =>
  !!name && name.trim().toLowerCase() === "grafiche decorative";

export interface ConfigState {
  category: string | null;
  model_id: string | null;
  model_other: string;
  design_id: string | null;
  design_other: string;
  is_colors_only: boolean;
  is_decorative: boolean;
  skip_details: boolean;
  area_id: string | null;
  type_id: string | null;
  detail_id: string | null;
  description: string;
  contact: { full_name: string; email: string; phone: string; privacy_accepted: boolean };
  stepIndex: number;
}

export const emptyState = (): ConfigState => ({
  category: null,
  model_id: null, model_other: "",
  design_id: null, design_other: "",
  is_colors_only: false,
  is_decorative: false,
  skip_details: false,
  area_id: null, type_id: null, detail_id: null,
  description: "",
  contact: { full_name: "", email: "", phone: "", privacy_accepted: false },
  stepIndex: 0,
});

const STORAGE_KEY = "bonker_configurator_v2";

export const saveDraft = (s: ConfigState) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
};
export const loadDraft = (): ConfigState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...emptyState(), ...JSON.parse(raw) } : null;
  } catch { return null; }
};
export const clearDraft = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
};

/** "Solo colori", "Baby" and "Grafiche decorative" skip area/realization/detail. */
const isSkipped = (key: StepKey, skipDetails: boolean) =>
  skipDetails && (key === "area" || key === "realization" || key === "detail");

export const visibleSteps = (skipDetails: boolean): StepKey[] =>
  STEP_KEYS.filter((k) => !isSkipped(k, skipDetails));

export const nextIndex = (i: number, skipDetails: boolean): number => {
  let j = i + 1;
  while (j < STEP_KEYS.length && isSkipped(STEP_KEYS[j], skipDetails)) j++;
  return Math.min(j, STEP_KEYS.length - 1);
};
export const prevIndex = (i: number, skipDetails: boolean): number => {
  let j = i - 1;
  while (j > 0 && isSkipped(STEP_KEYS[j], skipDetails)) j--;
  return Math.max(j, 0);
};

export const emailValid = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export const contactValid = (c: ConfigState["contact"]): boolean => {
  const hasName = !!c.full_name.trim();
  const emailOk = emailValid(c.email.trim());
  return hasName && emailOk && c.privacy_accepted;
};

export interface PriceResult { found: boolean; price_min: number | null; price_max: number | null; }

export async function estimatePrice(s: ConfigState): Promise<PriceResult> {
  const body = {
    category: s.category,
    is_colors_only: s.is_colors_only,
    is_decorative: s.is_decorative,
    area_id: (s.is_colors_only || s.is_decorative) ? null : s.area_id,
    type_id: (s.is_colors_only || s.is_decorative) ? null : s.type_id,
    detail_id: (s.is_colors_only || s.is_decorative) ? null : s.detail_id,
  };
  const { data } = await api.post("/price/estimate", body);
  return data;
}

export function formatPrice(p: PriceResult | null): string {
  if (!p || !p.found) return "Su richiesta";
  const round = (n: number) => `€${Math.round(n)}`;
  if (p.price_min != null && p.price_max == null) return `Da ${round(p.price_min)}`;
  if (p.price_min != null && p.price_max != null) return `${round(p.price_min)} – ${round(p.price_max)}`;
  return "Su richiesta";
}
